import { api } from "./client";

// Portal Materials Library — admin CRUD client. Mirrors the C# DTOs in
// src/PhoenixCapital.Core/DTOs/PortalMaterials/PortalMaterialDtos.cs.

export type MaterialAudience =
  | "Broker"
  | "Buyer"
  | "Seller"
  | "Vendor"
  | "Tps"
  | "Brokerage"
  // Onboarding differentiation — a collection-filing category only (see
  // COLLECTION_AUDIENCES in api/collectionTemplates.ts). Deliberately NOT added
  // to the shared MATERIAL_AUDIENCES array below, which drives the signup-wizard
  // account-type PROVISIONING picker + portal-materials publish. (The
  // portal-accounts type FILTER carries "Brokerage" via its own page-local list,
  // never this array.)
  | "JuniorBroker"
  | "All";

export const MATERIAL_AUDIENCES: MaterialAudience[] = [
  "Broker",
  "Buyer",
  "Seller",
  "Vendor",
  "Tps",
  "All",
];

export interface PortalMaterialDto {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  audience: MaterialAudience;
  originalFileName: string;
  contentType: string;
  sizeBytes: number;
  active: boolean;
  createdAt: string;
}

export interface UpdatePortalMaterialRequest {
  title: string;
  description: string | null;
  category: string | null;
  audience: MaterialAudience;
  active: boolean;
}

export interface PortalMaterialDownloadDto {
  url: string;
  expiresAt: string;
}

const BASE = "/portal-materials";

export const portalMaterialsApi = {
  list: (filter?: { includeInactive?: boolean; audience?: MaterialAudience | null }) =>
    api.get<PortalMaterialDto[]>(BASE, {
      includeInactive: filter?.includeInactive ?? true,
      audience: filter?.audience ?? undefined,
    }),

  upload: (file: File, metadata: {
    title: string;
    description?: string | null;
    category?: string | null;
    audience: MaterialAudience;
  }) => {
    const form = new FormData();
    form.append("title", metadata.title);
    if (metadata.description) form.append("description", metadata.description);
    if (metadata.category) form.append("category", metadata.category);
    form.append("audience", metadata.audience);
    form.append("file", file);
    return api.upload<PortalMaterialDto>(BASE, form);
  },

  update: (id: string, body: UpdatePortalMaterialRequest) =>
    api.put<PortalMaterialDto>(`${BASE}/${id}`, body),

  replaceFile: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.upload<PortalMaterialDto>(`${BASE}/${id}/file`, form);
  },

  delete: (id: string) => api.delete<void>(`${BASE}/${id}`),

  download: (id: string) =>
    api.get<PortalMaterialDownloadDto>(`${BASE}/${id}/download`),
};
